import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useTheme } from '../App';
import { usePageContent } from '../hooks/usePageContent';

const AboutPage = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { getContent } = usePageContent('about');

  return (
    <div style={{ 
      background: isDark ? '#0A1929' : '#f8fafc',
      minHeight: '100vh',
      transition: 'background-color 0.3s ease'
    }}>
      <Navbar />
      
      {/* Hero Section with Banner Image */}
      <section style={{ 
        position: 'relative',
        width: '100%',
        height: '400px',
        overflow: 'hidden',
        background: '#5b7fb8'
      }}>
        <img 
          src={getContent('image_url') || "/about-banner.png"}
          alt="Adhva Academy - Your Pathway to Success in Competitive Exams"
          onError={(e) => {
            console.error('Image failed to load. Make sure about-banner.png is in the public folder');
            e.target.style.display = 'none';
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            filter: isDark ? 'brightness(0.85)' : 'none'
          }}
        />
      </section>

      <section className="py-8 container">
        <div className="card" style={{ 
          maxWidth: '900px', 
          margin: '0 auto',
          background: isDark ? '#182B3A' : 'white',
          border: isDark ? '1px solid #1E3A52' : '1px solid #e0e0e0',
          boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ 
            fontSize: '1.125rem', 
            lineHeight: 1.8, 
            color: isDark ? '#B8C5D0' : '#4a5568',
            transition: 'color 0.3s ease'
          }}>
            <p style={{ marginBottom: '1.5rem' }}>
              Adhva Academy is a new-age learning platform dedicated to guiding aspirants in their journey towards success in <strong style={{ color: isDark ? '#FFB570' : '#5b7fb8' }}>UPSC, KPSC, SSC, Banking, and other competitive examinations</strong>.
            </p>
            <p style={{ marginBottom: '1.5rem' }}>
              Founded with the belief that every aspirant deserves the right guidance from the very beginning, Adhva Academy combines experienced faculty, structured study plans, and exam-focused resources to help students prepare with clarity and confidence.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8" style={{ 
        background: isDark 
          ? 'linear-gradient(135deg, #0F1922 0%, #182B3A 100%)' 
          : 'linear-gradient(135deg, #f9fafb 0%, #f0f0f0 100%)',
        transition: 'background 0.3s ease'
      }}>
        <div className="container">
          <div className="card" style={{ 
            maxWidth: '900px', 
            margin: '0 auto', 
            background: isDark ? '#182B3A' : 'white',
            border: isDark ? '1px solid #1E3A52' : '1px solid #e0e0e0',
            boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease'
          }}>
            <h2 style={{ 
              marginBottom: '1.5rem', 
              color: isDark ? '#FF8C38' : '#5b7fb8',
              transition: 'color 0.3s ease'
            }}>Our Story – The Meaning of Adhva</h2>
            <div style={{ 
              fontSize: '1.125rem', 
              lineHeight: 1.8, 
              color: isDark ? '#B8C5D0' : '#4a5568',
              transition: 'color 0.3s ease'
            }}>
              <p style={{ marginBottom: '1.5rem' }}>
                The word <strong style={{ color: isDark ? '#FFB570' : '#7c9ccc' }}>"Adhva"</strong> comes from Sanskrit, meaning <strong>"path"</strong> or <strong>"journey."</strong> We chose this name because we believe that exam success is not just about the destination but also about the path you take to reach it. At Adhva Academy, we walk alongside our students, making their journey more structured, focused, and purposeful.
              </p>
              <p style={{ marginBottom: '0' }}>
                We started this academy with a simple idea: to provide mentorship and support that they themselves wished for during their preparation years. Unlike crowded coaching systems, Adhva Academy was built to focus on individual learning needs, guided mentorship, and an inclusive student community.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 container">
        <h2 style={{ 
          marginBottom: '3rem', 
          textAlign: 'center', 
          fontSize: 'clamp(2rem, 4vw, 2.5rem)',
          color: isDark ? '#FF8C38' : '#5b7fb8',
          transition: 'color 0.3s ease'
        }}>What Makes Us Different?</h2>
        <div className="grid grid-3">
          <div className="card text-center" data-testid="value-0" style={{
            background: isDark ? '#182B3A' : 'white',
            border: isDark ? '1px solid #1E3A52' : '1px solid #e0e0e0',
            boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
            <h3 style={{ 
              marginBottom: '1rem', 
              color: isDark ? '#FFB570' : '#5b7fb8',
              transition: 'color 0.3s ease'
            }}>Student-first approach</h3>
            <p style={{ 
              color: isDark ? '#B8C5D0' : '#4a5568', 
              lineHeight: 1.7,
              transition: 'color 0.3s ease'
            }}>
              Smaller batches, personalized support, and mentorship that gives attention to every learner.
            </p>
          </div>
          <div className="card text-center" data-testid="value-1" style={{
            background: isDark ? '#182B3A' : 'white',
            border: isDark ? '1px solid #1E3A52' : '1px solid #e0e0e0',
            boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
            <h3 style={{ 
              marginBottom: '1rem', 
              color: isDark ? '#FFB570' : '#5b7fb8',
              transition: 'color 0.3s ease'
            }}>Structured learning path</h3>
            <p style={{ 
              color: isDark ? '#B8C5D0' : '#4a5568', 
              lineHeight: 1.7,
              transition: 'color 0.3s ease'
            }}>
              From NCERT basics to advanced topics, our study plans ensure no stage is left uncovered.
            </p>
          </div>
          <div className="card text-center" data-testid="value-2" style={{
            background: isDark ? '#182B3A' : 'white',
            border: isDark ? '1px solid #1E3A52' : '1px solid #e0e0e0',
            boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎯</div>
            <h3 style={{ 
              marginBottom: '1rem', 
              color: isDark ? '#FFB570' : '#5b7fb8',
              transition: 'color 0.3s ease'
            }}>Mentorship beyond teaching</h3>
            <p style={{ 
              color: isDark ? '#B8C5D0' : '#4a5568', 
              lineHeight: 1.7,
              transition: 'color 0.3s ease'
            }}>
              We don't just deliver lectures; we mentor, motivate, and support aspirants throughout their journey.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8" style={{ 
        background: isDark 
          ? 'linear-gradient(135deg, #0F1922 0%, #0F4C75 100%)' 
          : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)', 
        color: isDark ? '#E8EEF2' : '#333',
        transition: 'all 0.3s ease'
      }}>
        <div className="container text-center">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <p style={{ 
              fontSize: '1.3rem', 
              lineHeight: 1.8, 
              marginBottom: '1.5rem', 
              opacity: 0.95,
              transition: 'color 0.3s ease'
            }}>
              At Adhva Academy, we are not just preparing students for exams—<strong>we are preparing them for a transformative journey of learning and growth</strong>.
            </p>
            <p style={{ 
              fontSize: '1.25rem', 
              lineHeight: 1.8, 
              marginBottom: '1rem', 
              fontWeight: 600,
              transition: 'color 0.3s ease'
            }}>
              Your success is our mission, and your journey is our commitment.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 container text-center">
        <h2 style={{ 
          marginBottom: '1.5rem', 
          color: isDark ? '#FF8C38' : '#5b7fb8',
          transition: 'color 0.3s ease'
        }}>Ready to Begin Your Journey?</h2>
        <p style={{ 
          color: isDark ? '#B8C5D0' : '#4a5568', 
          marginBottom: '2rem', 
          fontSize: '1.125rem',
          transition: 'color 0.3s ease'
        }}>
          Join hundreds of aspirants who have chosen excellence
        </p>
        <a 
          href="https://forms.gle/5mjmuxov7LaYCHTU6" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="btn-primary" 
          style={{ 
            fontSize: '1.1rem', 
            padding: '1rem 2.5rem',
            background: isDark 
              ? 'linear-gradient(135deg, #FF8C38 0%, #FFB570 100%)' 
              : 'linear-gradient(135deg, #5b7fb8 0%, #7c9ccc 100%)',
            border: 'none',
            boxShadow: isDark 
              ? '0 4px 12px rgba(255, 140, 56, 0.3)' 
              : '0 4px 12px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.3s ease'
          }}
        >
          Enroll Now →
        </a>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;